import { getTooltipAttributes } from "@common/contexts/Tooltip";
import { getTourAttributes } from "@common/contexts/Tour";
import Icon from "@common/controls/Icon/component";
import InfiniteScroll from "@common/controls/InfiniteScroll/component";
import Menu, { type MenuContentProps } from "@common/controls/Menu/component";
import Preloader from "@common/controls/Preloader/component";
import { IconType, SystemColor, type WithChildren } from "@common/declarations";
import KeyboardNavigation, {
  type KeyboardNavigationChildrenArgs,
  type KeyboardNavigationMode
} from "@common/hocs/keyboardNavigation";
import withDropdownHOC, {
  type WithDropdownCmpProps,
  type WithDropdownComponentAddedProps,
  type WithDropdownToggleProps
} from "@common/hocs/withDropdown";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import { useI18n } from "@common/hooks/i18n";
import { EMPTY_ARRAY } from "@common/lib/consts";
import { identityPromise } from "@common/lib/misc";
import { Common } from "@common/types";
import { default as classnames } from "classnames";
import { identity } from "lodash";
import React from "react";
import withAddHOC from "../hocs/withAdd";

export type SortOrder = "asc" | "desc";

type ToggleProps = {
  className?: string;
  tourStep?: string;
  htmlTitle?: string;
  icon?: string;
  iconSize?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  isNoOptions?: boolean;
  loadingText?: string;
  name?: string;
  noArrow?: boolean;
  prepend?: any;
  qa?: string;
  style?: React.CSSProperties;
  tabIndex?: number;
  theme?: "borderless";
  title: any;
  tooltip?: string;

  onBlur?: () => void;
  onFocus?: () => void;
  onClick?: (e: React.MouseEvent) => void;
  toggle?: any;
};

export type SelectBaseProps<T> = ToggleProps &
  Omit<ContentBaseProps, "children"> & {
    items: T[];
    getIsActive?: (item: T) => boolean;
    renderItem: (item: T) => any;
    onItemClick: (item: T) => void;
  };

export const Toggle = (props: ToggleProps & WithDropdownToggleProps) => {
  const i18n = useI18n();

  return (
    <div
      ref={props.toggleRef}
      data-name={props.qa || props.name}
      tabIndex={props.tabIndex ?? 0}
      className={classnames("select", props.className, props.theme, {
        disabled: props.isDisabled,
        empty: props.isNoOptions,
        loading: props.isLoading,
        no_arrow: props.noArrow,
        open: props.isOpen
      })}
      onClick={props.onClick ?? (!props.isDisabled && !props.isNoOptions ? props.toggleOpen : () => {})}
      style={props.style}
      title={props.htmlTitle}
      unselectable="on"
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      {...getTooltipAttributes(props.isOpen ? undefined : props.tooltip)}
      {...getTourAttributes({ step: props.tourStep })}
    >
      {props.toggle ? (
        props.toggle
      ) : (
        <>
          <span className="content single-line-ellipsis">
            {props.isLoading ? (
              <>{props.loadingText ?? i18n.loading}</>
            ) : props.isNoOptions ? (
              props.title || i18n.noOptionsAvailable
            ) : (
              <>
                {!!props.icon && <Icon icon={props.icon} size={props.iconSize} />}

                {props.prepend}

                {!!props.title || props.title === 0 ? (
                  typeof props.title === "string" ? (
                    <span className="single-line-ellipsis" dangerouslySetInnerHTML={{ __html: props.title }}></span>
                  ) : (
                    props.title
                  )
                ) : null}
              </>
            )}
          </span>

          {props.isLoading ? (
            <Preloader isTransparent isFontSize />
          ) : props.noArrow ? null : (
            <Icon className="toggle" icon={props.isOpen ? IconType.ChevronUp : IconType.ChevronDown} />
          )}
        </>
      )}
    </div>
  );
};

export interface SelectHeading {
  content: any;
  hint?: string;
  onClick?: () => void;
  isSearch?: boolean;
  isToggle?: boolean; // if true - closes the dropdown
}

export type FooterProps = {
  close: () => void;
};

export type ContentHeight = number | "auto";
export type ContentBaseProps = {
  children: any;
  className?: string;
  footer?: (props: FooterProps) => any;
  hasMore: boolean;
  heading?: SelectHeading | SelectHeading[];
  height?: ContentHeight;
  infiniteLoaderKey?: string;
  keyboardNavigationMode?: KeyboardNavigationMode;
  loadMore: () => Promise<void>;
  onItemSelected?: (item: any) => void;
  options: Common.OptionItem<any>[];
  tag?: string;
};
export const ContentBase = ({
  children,
  className,
  close,
  footer,
  hasMore,
  heading,
  height,
  keyboardNavigationMode,
  infiniteLoaderKey,
  loadMore,
  onItemSelected,
  options,
  tag
}: ContentBaseProps & WithDropdownCmpProps) => {
  const headings = React.useMemo(() => (heading ? (Array.isArray(heading) ? heading : [heading]) : null), [heading]);

  const onHeadingClick = React.useCallback(
    (index: number) => {
      const { isToggle, onClick } = headings![index];
      isToggle && close();
      onClick && onClick();
    },
    [heading, close]
  );

  return (
    <>
      {!!headings &&
        headings.map((x, i) => (
          <HeadingSelectItem key={i} isClickable={!!x.onClick} hint={x.hint} onClick={() => onHeadingClick(i)}>
            {x.content}
          </HeadingSelectItem>
        ))}

      <KeyboardNavigation
        mode={keyboardNavigationMode ?? "default"}
        items={options}
        onItemSelected={onItemSelected ?? identity}
      >
        {({ ref }: KeyboardNavigationChildrenArgs) => (
          <InfiniteScroll
            ref={ref}
            key={infiniteLoaderKey ?? "loader"}
            className={classnames("select_options", className)}
            element={tag ?? "ul"}
            hasMore={hasMore}
            loader={<Preloader isFontSize relative size={30} />}
            loadMore={loadMore}
            tabIndex={0}
            height={height === "auto" ? undefined : height ?? 200}
          >
            {children}
          </InfiniteScroll>
        )}
      </KeyboardNavigation>

      {footer && <div className="footer">{footer({ close })}</div>}
    </>
  );
};

export type SelectStaticContentProps = WithDropdownComponentAddedProps & {
  children: any;
  heading?: SelectHeading[];
  height?: ContentHeight;
};
export class SelectStaticContent extends React.PureComponent<WithDropdownCmpProps & SelectStaticContentProps> {
  public render() {
    const { children, ...props } = this.props;

    return (
      <ContentBase
        {...props}
        className="select_static"
        hasMore={false}
        loadMore={identityPromise}
        options={EMPTY_ARRAY}
        tag="ul"
      >
        {React.Children.map<React.ReactElement<any> | null, React.ReactElement<SelectItemProps>>(children, (x, i) =>
          x
            ? React.cloneElement(x, {
                ...x.props,
                key: i,
                onClick: (e) => {
                  e?.stopPropagation();

                  if (!x.props.isDisabled && x.props.onClick) {
                    x.props.onClick();
                  }

                  props.close();
                }
              })
            : null
        )}
      </ContentBase>
    );
  }
}

export const SelectItemSeparator = () => <div className="separator" />;

export type SelectItemProps = Partial<React.HTMLProps<HTMLLIElement>> & {
  isActive?: boolean;
  isDisabled?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  children: any;
  className?: string;
  orientation?: "horizontal" | "vertical";
  name?: string;
  qa?: string;
  tooltip?: string | null;
};
export const SelectItem = (props: SelectItemProps) => (
  <li
    ref={props.ref}
    className={classnames("item", props.className, props.orientation, {
      active: props.isActive,
      disabled: props.isDisabled,
      selected: props.isActive
    })}
    onClick={props.isDisabled || !props.onClick ? undefined : (e) => (e.stopPropagation(), props.onClick!(e))}
    onMouseDown={props.onMouseDown}
    data-name={props.qa ?? props.name}
  >
    <div className="item-content" {...getTooltipAttributes(props.tooltip)}>
      {props.children}
    </div>
  </li>
);

type GroupSelectItemProps = {
  caption: string;
  name?: string;
  qa?: string;
};
export const GroupSelectItem = (props: GroupSelectItemProps) => (
  <div className="group" data-name={props.qa ?? props.name}>
    <div className="group_content">
      <span className="caption hint">{props.caption}</span>
    </div>
  </div>
);

type IconSelectItemProps = Omit<SelectItemProps, "children"> & {
  icon?: string;
  iconColor?: SystemColor;
  caption: string;
};
export const IconSelectItem = ({ icon, iconColor, caption, ...props }: IconSelectItemProps) => (
  <SelectItem {...props}>
    {!!icon && <Icon color={iconColor} icon={icon} />}
    {caption}
  </SelectItem>
);

type HeadingSelectItemProps = WithChildren & {
  hint?: any;
  isDisabled?: boolean;
  isClickable: boolean;
  onClick?: () => void;
  qa?: string;
};
export const HeadingSelectItem = (props: HeadingSelectItemProps) => (
  <div
    className={classnames("heading", { pointer: props.isClickable })}
    onClickCapture={props.onClick}
    data-name={props.qa}
  >
    <div className="heading_content">{props.children}</div>

    {!!props.hint && <p className="hint">{props.hint}</p>}
  </div>
);

type SelectItemWithMenuProps<T extends object> = SelectItemProps & {
  item: T;
  MenuContent?: React.ComponentType<MenuContentProps<T>>;
  menuName: string;
};
export const SelectItemWithMenu = <T extends object>({
  children,
  item,
  MenuContent,
  menuName,
  ...props
}: SelectItemWithMenuProps<T>) => (
  <SelectItem {...props}>
    {children}

    {!!MenuContent && (
      <Menu
        align={ContentAlign.BottomRight}
        Content={MenuContent}
        dropdownClassName="no-close-click-outside"
        item={item}
        name={menuName}
        noArrow
        theme="white"
        toggleIcon={Common.IconType.EllipsisHorizontal}
      />
    )}
  </SelectItem>
);

export type SelectStaticProps = SelectStaticContentProps & ToggleProps;
const SelectStaticBase = withDropdownHOC<SelectStaticProps, SelectStaticContentProps>(Toggle, SelectStaticContent);

export const SelectStatic = React.memo((props: SelectStaticProps) => (
  <SelectStaticBase
    {...props}
    dropdownName={props.qa || props.name ? `${props.qa || props.name}_dropdown` : undefined}
  />
));

export const SelectStaticWithAdd = withAddHOC(SelectStatic);
